/**
 * SimpleLayout — 보호된 라우트 공용 쉘
 *
 * Phase 1 개편:
 *  - 기존 `Sidebar`(ColdGray bg `#f2f3f5`)를 `AppSidebar`로 교체
 *  - 신규 `TopUtilityBar`(브레드크럼 + 검색 + 알림 + 유저) 상단 고정
 *  - `SystemModeProvider`는 index.tsx 레벨에 위치
 *  - 전환 애니메이션(`switching`) 에 맞춰 MainContent에 opacity/translateY 적용
 *
 * 기존 Sidebar/TopNavigationBar 파일은 이번 Phase에서 **삭제하지 않음**(하위 호환 안전장치).
 *
 * 2026-10-14 워크스페이스 디자인: 회색 바탕(.ws-shell) 위 사이드바 + 흰 본문 패널(.ws-panel, 크게 둥근 모서리).
 *   ⛔ 스크롤은 지금처럼 창(window)이 한다 — 패널 안 스크롤로 바꾸면 sticky·무한 스크롤·scrollTo 쓰는 화면이 깨진다.
 */

import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AppSidebar from '../layout/AppSidebar';
import TopUtilityBar from '../layout/TopUtilityBar';
import CommandPalette from '../layout/CommandPalette';
import HotkeyHelpModal from '../layout/HotkeyHelpModal';
import { useSystemMode } from '../../context/SystemModeContext';

const NARROW_QUERY = '(max-width: 1023px)';

const SimpleLayout: React.FC = () => {
  const location = useLocation();
  // 좁은 화면(1024 미만)에서는 사이드바를 아이콘만 남기고 접는다 — 경계를 넘을 때만 바꾸고, 그 사이 손으로 연 것은 존중한다
  const [sidebarExpanded, setSidebarExpanded] = React.useState(() => !window.matchMedia?.(NARROW_QUERY).matches);
  React.useEffect(() => {
    const mq = window.matchMedia?.(NARROW_QUERY);
    if (!mq) return undefined;
    const onChange = (e: MediaQueryListEvent) => setSidebarExpanded(!e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const { switching, mode } = useSystemMode();

  // Sprint 23: ⌘K / Ctrl+K 글로벌 검색 팔레트
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  // Sprint 36: ? 단축키 도움말 모달
  const [hotkeyHelpOpen, setHotkeyHelpOpen] = React.useState(false);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        // input/textarea 안에서도 트리거 (보편적 UX)
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      // Sprint 36: ? 키 — 입력 필드 포커스 외에서만
      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const target = e.target as HTMLElement | null;
        const tag = (target?.tagName || '').toLowerCase();
        const isEditable = target?.isContentEditable || ['input', 'textarea', 'select'].includes(tag);
        if (isEditable) return;
        e.preventDefault();
        setHotkeyHelpOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 브라우저 탭 타이틀
  const getPageTitle = React.useCallback(() => {
    const p = location.pathname;
    if (p.startsWith('/hr')) {
      if (p === '/hr' || p === '/hr/me') return '내 프로필';
      if (p === '/hr/dashboard') return 'HR 대시보드';
      if (p === '/hr/search') return '구성원 검색';
      if (p === '/hr/org-chart') return '조직도';
      if (p === '/hr/onboarding') return '온·오프보딩';
      return 'HR';
    }
    if (p.includes('/production')) return '생산 관리';
    if (p.includes('/purchase')) return '구매 관리';
    if (p.includes('/contacts')) return '거래처 관리';
    if (p.includes('/inventory')) return '재고 관리';
    if (p.includes('/sales')) return '판매 관리';
    if (p.includes('/accounting')) return '회계 관리';
    if (p.includes('/crm')) return 'CRM';
    if (p.includes('/analytics')) return '분석 리포트';
    if (p.includes('/settings')) return '설정';
    if (p.includes('/dashboard')) return '대시보드';
    if (p.includes('/bom')) return 'BOM 관리';
    return 'MARYHOUSE ADMIN';
  }, [location.pathname]);

  React.useEffect(() => {
    const title = getPageTitle();
    const suffix = mode === 'hr' ? 'MARYHOUSE HR' : 'MARYHOUSE ERP';
    document.title = `${title} | ${suffix}`;
  }, [location.pathname, getPageTitle, mode]);

  return (
    <div className="ws-shell">
      <AppSidebar
        collapsed={!sidebarExpanded}
        onToggleCollapse={() => setSidebarExpanded((v) => !v)}
      />
      <div className="ws-main">
        <div className="ws-panel">
          <TopUtilityBar />
          <div
            id="main-content-area"
            style={{
              flex: 1,
              minWidth: 0,
              opacity: switching ? 0 : 1,
              // 전환 중이 아닐 때는 transform 미적용 — translateY(0) 도 fixed 자식의 containing block 을
              // 만들어 NotionSidePeek 등 position:fixed 모달이 viewport 가 아닌 이 요소 기준으로 렌더되는 회귀.
              transform: switching ? 'translateY(6px)' : 'none',
              transition: 'opacity 240ms ease, transform 240ms ease',
            }}
          >
            <Outlet />
          </div>
        </div>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <HotkeyHelpModal open={hotkeyHelpOpen} onClose={() => setHotkeyHelpOpen(false)} />
    </div>
  );
};

export default SimpleLayout;
